import React, { useMemo, useState } from "react";
import { createBrandForCustomer } from "../../../services/customerService";
import { Customer } from "../../../types/customer";
import { debug } from "../../../utils/debugLogger";

/** Sage focus ring — same as InviteUserSection. */
const CONTROL_FOCUS =
  "focus:outline-none focus-visible:outline-none focus:border-theme-secondary focus:ring-2 focus:ring-theme-secondary/40 caret-theme-fg";
const LABEL_UPPER =
  "mb-1.5 block text-xs font-medium uppercase tracking-wide text-theme-fg-muted";

interface Props {
  idToken: string | null;
  /** Every Brand, as the page already loads them; the Agencies are picked from it. */
  customers: Customer[];
  /** Called with the new Brand's id so the page can list and select it. */
  onCreated: (customerId: string) => void | Promise<void>;
}

const EMPTY = {
  businessName: "",
  city: "",
  state: "",
  country: "USA",
  zipCode: "",
  ownerEmail: "",
  ownerName: "",
  initialCredits: "10",
  agencyId: "",
};

/**
 * SuperAdmin "create for customer" on /users/all (SGL-555). Done-for-you Brands
 * used to land with the staff member as their only seat; this names the paying
 * customer instead, so they are the Owner from the start. The server checks
 * SuperAdmin and the Agency, sets the contact email, and sends a new login its
 * password email. The admin names the Agency (never inferred). The customer's
 * plan credit applies while this Brand is within their paid Brand limit;
 * otherwise the admin's amount does (#259 review).
 */
const CreateBrandForCustomerSection: React.FC<Props> = ({ idToken, customers, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  // An Agency is a Brand that other Brands name as theirs (common.seats).
  const agencies = useMemo(() => {
    const ids = new Set(customers.map((c) => c.agency_id).filter(Boolean));
    return customers.filter((c) => ids.has(c.customer_id));
  }, [customers]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const set = (key: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!idToken) {
      setMessage({ type: "error", text: "You are not signed in." });
      return;
    }
    const businessName = form.businessName.trim();
    const ownerEmail = form.ownerEmail.trim();
    try {
      setLoading(true);
      const res = await createBrandForCustomer(
        {
          businessName,
          city: form.city.trim(),
          state: form.state.trim(),
          country: form.country.trim(),
          zipCode: form.zipCode.trim() || undefined,
          ownerEmail,
          ownerName: form.ownerName.trim(),
          initialCredits: parseFloat(form.initialCredits) || 0,
          agencyId: form.agencyId || undefined,
        },
        idToken
      );
      if (!res.owner) {
        // A customer-management older than SGL-555 ignores owner_email: the
        // Brand exists but the customer is not on it. Say so, never "done".
        setMessage({
          type: "error",
          text: `Created ${businessName}, but ${ownerEmail} was not made Owner. Open it under Credits & Billing and invite them as Owner from the Team card.`,
        });
        await onCreated(res.customer_id);
        return;
      }
      const login = res.owner.invited
        ? `${ownerEmail} got an email with a temporary password.`
        : `${ownerEmail} already had a login, so no new email was sent; they'll see the Brand next time they sign in.`;
      const agencyName = agencies.find((a) => a.customer_id === res.agency_id)?.business_name;
      const agency = res.agency_id ? `Operated by ${agencyName || res.agency_id}.` : "Not linked to an Agency.";
      const credit = res.credit_source === "plan" ? "from their plan" : "the amount you set";
      setMessage({
        type: "success",
        text: `Created ${businessName} with ${ownerEmail} as Owner. ${login} Opening credit $${Number(res.credit_balance ?? 0).toFixed(2)} (${credit}). ${agency} You keep a team seat on it.`,
      });
      setForm(EMPTY);
      await onCreated(res.customer_id);
    } catch (err) {
      debug.error("CreateBrandForCustomerSection:", err);
      setMessage({ type: "error", text: err instanceof Error ? err.message : "Couldn't create the Brand." });
      const createdId = (err as { customerId?: string }).customerId;
      if (createdId) await onCreated(createdId);
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = `h-10 w-full rounded-md border border-theme-border bg-theme-background px-3 text-sm text-theme-fg ${CONTROL_FOCUS} disabled:opacity-60 placeholder:text-slate-400`;
  const field = (key: keyof typeof EMPTY, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div>
      <label htmlFor={`cfc-${key}`} className={LABEL_UPPER}>
        {label}
        {props.required && <span className="text-red-600"> *</span>}
      </label>
      <input id={`cfc-${key}`} value={form[key]} onChange={set(key)} disabled={loading} className={fieldClass} {...props} />
    </div>
  );

  return (
    <div className="rounded-lg border border-theme-border bg-theme-surface p-6 shadow-sm">
      <div className="mb-4 flex items-start gap-2">
        <span className="material-icons shrink-0 text-xl text-theme-fg-muted" aria-hidden>
          add_business
        </span>
        <div>
          <h3 className="text-base font-semibold text-theme-fg">Create a Brand for a customer</h3>
          <p className="mt-1 text-sm text-theme-fg-muted">
            For a paying done-for-you customer. They become the Owner of the new Brand.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {field("ownerEmail", "Customer email", { type: "email", required: true, autoComplete: "off", placeholder: "owner@theirbusiness.com" })}
          {field("ownerName", "Customer name", { required: true, autoComplete: "off", placeholder: "Full name" })}
          {field("businessName", "Business name", { required: true, placeholder: "Their business" })}
          {field("city", "City", { required: true })}
          {field("state", "State", { required: true, placeholder: "TX" })}
          {field("country", "Country", { required: true })}
          {field("zipCode", "ZIP code", { inputMode: "numeric" })}
          {field("initialCredits", "Opening credit if past their plan ($)", { type: "number", min: 0, step: "0.01", required: true })}
          <div className="md:col-span-2">
            <label htmlFor="cfc-agencyId" className={LABEL_UPPER}>
              Operated by (Agency)
            </label>
            <select id="cfc-agencyId" value={form.agencyId} onChange={set("agencyId")} disabled={loading} className={fieldClass}>
              <option value="">No Agency</option>
              {agencies.map((a) => (
                <option key={a.customer_id} value={a.customer_id}>
                  {a.business_name || a.customer_id}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-theme-fg-muted">
              The Agency's team can work on the Brand. You keep a team seat on it either way.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            type="submit"
            disabled={loading}
            className="h-10 rounded-md bg-theme-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-theme-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Creating…" : "Create Brand and invite Owner"}
          </button>
        </div>
      </form>

      {message && (
        <div
          role="status"
          className={`mt-4 rounded-md border p-3 text-sm ${
            message.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

export default CreateBrandForCustomerSection;
